import { useEffect, useState } from 'react';
import { TabLabelRows } from 'src/components/GlobalTabs/tab-labels/tab-label-rows';
import { useSelectedConversation } from 'src/pages/Conversations';
import { useGlobalStore } from 'src/store';
import { ConversationTabEntity, TabType, useGlobalTabs } from 'src/utils/hooks';

type ConversationLabelProps = ConversationTabEntity['entity'];

type ConversationLabelState = ConversationLabelProps & { partnerName: string };

// ConversationLabel receives preserved information about the tab and shows it as an initial view while loading fresh
// data from the server and replacing it. It guarantees that users don't need to wait for data from the server to
// interact with tabs.
export const ConversationLabel = (props: ConversationLabelProps) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { selectedConversation, isLoading } = useSelectedConversation({
    conversationId: props.conversationId,
  });

  const [state, setState] = useState<ConversationLabelState>({
    ...props,
    partnerName: '',
  });

  const { activeTab, closeTab, findTab } = useGlobalTabs();
  const isActive =
    activeTab?.id === props.conversationId && activeTab?.type === TabType.Conversation;

  useEffect(() => {
    if (!isLoading && !!selectedConversation) {
      const { subject, unseenCount, partnerId, friendlyId, partner, conversationId } =
        selectedConversation;
      setState({
        id: conversationId,
        subject,
        partnerName: partner?.businessName ?? '',
        unseenCount,
        partnerId,
        friendlyId,
        conversationId,
      });
    }

    // Close inactive tab if there's no conversation with specified Id. It is important to avoid
    // closing active tab as it is handled by ConversationExpanded component at the moment when
    // conversation doesn't exist anymore, but inactive tabs aren't linked to ConversationExpanded.
    if (!isLoading && !selectedConversation && !isActive) {
      setTimeout(() => {
        const tab = findTab(props.conversationId, TabType.Conversation);
        if (tab) {
          closeTab(tab);
        }
      }, 500);
    }
  }, [selectedConversation, isLoading, isActive, findTab, props.conversationId, closeTab]);

  const { friendlyId, unseenCount = 0, partnerName, subject } = state;

  return (
    <TabLabelRows
      tooltip={[friendlyId, subject].filter(Boolean).join(' · ')}
      overline={selectedPartnerId ? undefined : partnerName}
      id={friendlyId}
      title={subject}
      unseen={unseenCount > 0}
    />
  );
};
